import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Loader2,
  Pencil,
  Check,
  Building2,
  ShieldCheck,
  BadgeCheck,
  KeyRound,
  Trash2,
  AlertTriangle,
  Clock,
  LogOut,
} from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { t, dateLocale } from "i18n";
import Modal from "components/common/Modal";
import { showToast } from "utils/toast";
import betaAPI from "v2/api";
import Toggle from "../common/Toggle";
import ConfirmDialog from "../common/ConfirmDialog";
import { apiErrorMessage } from "utils/apiErrors";

// ── helpers ─────────────────────────────────────────────────────────

const fmtErr = (err, fallback = t("common.errors.generic")) =>
  apiErrorMessage(err, fallback);

const fmtDateTime = (v) => {
  if (!v) return t("admin.superUser.userDetail.never");
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString(dateLocale("en-US"), {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(dateLocale("en-US"), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const initials = (f, l) =>
  ((f?.[0] || "") + (l?.[0] || "")).toUpperCase() || "?";

const fullName = (u) =>
  [u?.fname, u?.lname].filter(Boolean).join(" ") || t("common.status.unknown");

// ── small building blocks ───────────────────────────────────────────

const Field = ({ label, children }) => (
  <div>
    <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
      {label}
    </p>
    <div className="text-[13px] text-[#374151]">{children}</div>
  </div>
);

const Input = ({ label, value, onChange, type = "text", placeholder }) => (
  <div>
    <label className="mb-1.5 block text-[13px] font-medium text-[#374151]">
      {label}
    </label>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-[10px] border border-[#E5E7EB] px-3.5 py-2.5 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-colors duration-150 focus:border-[#E07A3A]"
    />
  </div>
);

// ── activity timeline ───────────────────────────────────────────────

const ActivityTimeline = ({ userId }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const PAGE_SIZE = 15;

  const load = useCallback(
    async (p) => {
      const res = await betaAPI.getAdminUserActivity({
        userId,
        page: p,
        pageSize: PAGE_SIZE,
      });
      const results = res.data?.results || [];
      const total = res.data?.total_count ?? results.length;
      setHasMore(p * PAGE_SIZE < total);
      return results;
    },
    [userId]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPage(1);
    load(1)
      .then((r) => active && setItems(r))
      .catch(() => active && setItems([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [load]);

  const loadMore = async () => {
    const next = page + 1;
    setLoadingMore(true);
    try {
      const r = await load(next);
      setItems((prev) => [...prev, ...r]);
      setPage(next);
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 size={18} className="animate-spin text-[#E07A3A]" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="py-4 text-center text-[13px] text-[#9CA3AF]">
        {t("admin.superUser.userDetail.noActivity")}
      </p>
    );
  }

  return (
    <div>
      {items.map((a, i) => (
        <div key={a.id ?? i} className="relative flex gap-3 pb-4">
          {i !== items.length - 1 && (
            <span className="absolute left-[11px] top-6 h-full w-px bg-[#F3F4F6]" />
          )}
          <span className="z-10 mt-1 h-[22px] w-[22px] flex-shrink-0 rounded-full border border-[#F3F4F6] bg-white">
            <span className="flex h-full w-full items-center justify-center">
              <Clock size={11} className="text-[#9CA3AF]" />
            </span>
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-[13px] font-medium text-[#111827]">
                {a.activity_type_display || a.activity_type}
              </p>
              <span className="flex-shrink-0 text-[11px] text-[#9CA3AF]">
                {fmtDateTime(a.created_at)}
              </span>
            </div>
            {a.resource_name && (
              <p className="truncate text-[12px] text-[#6B7280]">
                {a.resource_name}
              </p>
            )}
            {a.workspace_name && (
              <p className="text-[11px] text-[#9CA3AF]">{a.workspace_name}</p>
            )}
          </div>
        </div>
      ))}

      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#E5E7EB] py-2 text-xs font-medium text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-60"
        >
          {loadingMore && <Loader2 size={13} className="animate-spin" />}
          {t("admin.superUser.userDetail.loadOlderActivity")}
        </button>
      )}
    </div>
  );
};

// ── the modal ───────────────────────────────────────────────────────

const UserDetailModal = ({
  userId,
  show,
  onClose,
  onChanged,
  currentUserId,
}) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const handleSignOutEverywhere = async () => {
    setConfirmSignOut(false);
    try {
      const { data } = await betaAPI.adminSignOutUserEverywhere({ userId });
      showToast.success(
        t("admin.superUser.userDetail.signedOutEverywhere", {
          count: data.sessions_ended,
        })
      );
    } catch (err) {
      console.error("Failed to sign user out everywhere", err);
      showToast.error(t("admin.superUser.userDetail.signOutEverywhereFailed"));
    }
  };
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState(null);

  const isSelf = detail && currentUserId && detail.id === currentUserId;

  // Load detail whenever the modal opens for a user.
  useEffect(() => {
    if (!show || !userId) return;
    let active = true;
    setLoading(true);
    setLoadError(null);
    setEditing(false);
    setConfirmDelete(false);
    setDeleteError(null);
    setConfirmReset(false);
    setResetError(null);
    betaAPI
      .getAdminUser({ userId })
      .then((res) => active && setDetail(res.data))
      .catch(
        (err) =>
          active &&
          setLoadError(fmtErr(err, t("admin.superUser.userDetail.loadFailed")))
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [show, userId]);

  const startEdit = () => {
    setForm({
      fname: detail.fname || "",
      lname: detail.lname || "",
      email: detail.email || "",
      is_superadmin: !!detail.is_superadmin,
      is_verified: !!detail.is_verified,
    });
    setSaveError(null);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    // Send only changed fields (empty body is a valid no-op, but this keeps
    // PATCHes tight and avoids re-validating an unchanged email).
    const body = {};
    ["fname", "lname", "email", "is_superadmin", "is_verified"].forEach((k) => {
      if (form[k] !== detail[k]) body[k] = form[k];
    });
    try {
      const res = await betaAPI.updateAdminUser({ userId, body });
      setDetail(res.data);
      onChanged?.(res.data);
      setEditing(false);
      showToast.success(t("admin.superUser.userDetail.updated"));
    } catch (err) {
      setSaveError(fmtErr(err, t("admin.superUser.userDetail.updateFailed")));
    } finally {
      setSaving(false);
    }
  };

  const sendReset = async () => {
    setResetting(true);
    setResetError(null);
    try {
      const res = await betaAPI.sendAdminUserPasswordReset({ userId });
      showToast.success(
        res.data?.detail || t("admin.superUser.userDetail.resetSent")
      );
      setConfirmReset(false);
    } catch (err) {
      setResetError(fmtErr(err, t("admin.superUser.userDetail.resetFailed")));
    } finally {
      setResetting(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await betaAPI.deleteAdminUser({ userId });
      showToast.success(t("admin.superUser.userDetail.deleted"));
      onChanged?.(null); // signal removal
      onClose();
    } catch (err) {
      setDeleteError(fmtErr(err, t("admin.superUser.userDetail.deleteFailed")));
    } finally {
      setDeleting(false);
    }
  };

  const closeIfIdle = () => {
    if (saving || deleting || resetting) return;
    onClose();
  };

  return (
    <Modal show={show} onClose={closeIfIdle} maxWidth="620px">
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={22} className="animate-spin text-[#E07A3A]" />
        </div>
      ) : loadError ? (
        <div className="p-6 text-center">
          <p className="text-sm text-[#DC2626]">{loadError}</p>
          <button
            onClick={onClose}
            className="mt-4 text-sm font-medium text-[#E07A3A]"
          >
            {t("common.actions.close")}
          </button>
        </div>
      ) : detail ? (
        <div className="flex max-h-[85vh] flex-col">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-[#F3F4F6] p-5">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#E07A3A]">
                <span className="text-sm font-semibold text-white">
                  {initials(detail.fname, detail.lname)}
                </span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-[17px] font-semibold text-[#111827]">
                    {fullName(detail)}
                  </h2>
                  {detail.is_superadmin && (
                    <span className="flex items-center gap-1 rounded bg-[#FEE2E2] px-1.5 py-0.5 text-[10px] font-semibold text-[#DC2626]">
                      <ShieldCheck size={11} />
                      {t("admin.superUser.userDetail.superAdminBadge")}
                    </span>
                  )}
                  <span
                    className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                      detail.is_verified
                        ? "bg-[#DCFCE7] text-[#16A34A]"
                        : "bg-[#FEF3C7] text-[#B45309]"
                    }`}
                  >
                    <BadgeCheck size={11} />
                    {detail.is_verified
                      ? t("admin.superUser.userDetail.verified")
                      : t("admin.superUser.userDetail.unverified")}
                  </span>
                </div>
                <p className="truncate text-[13px] text-[#6B7280]">
                  {detail.email}
                </p>
              </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1">
              {!editing && (
                <button
                  onClick={startEdit}
                  title={t("admin.superUser.userDetail.editUser")}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] transition duration-150 hover:bg-[#F3F4F6] hover:text-[#111827]"
                >
                  <Pencil size={15} />
                </button>
              )}
              <button
                onClick={closeIfIdle}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] transition duration-150 hover:bg-[#F3F4F6] hover:text-[#374151]"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            {editing ? (
              // ── Edit form ──
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label={t("admin.superUser.userDetail.firstName")}
                    value={form.fname}
                    onChange={(v) => setForm((f) => ({ ...f, fname: v }))}
                  />
                  <Input
                    label={t("admin.superUser.userDetail.lastName")}
                    value={form.lname}
                    onChange={(v) => setForm((f) => ({ ...f, lname: v }))}
                  />
                </div>
                <Input
                  label={t("admin.superUser.userDetail.email")}
                  type="email"
                  value={form.email}
                  onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                />
                <Toggle
                  label={t("admin.superUser.userDetail.superAdminToggle")}
                  hint={t("admin.superUser.userDetail.superAdminHint")}
                  checked={form.is_superadmin}
                  onChange={(v) => setForm((f) => ({ ...f, is_superadmin: v }))}
                />
                <Toggle
                  label={t("admin.superUser.userDetail.verified")}
                  hint={t("admin.superUser.userDetail.verifiedHint")}
                  checked={form.is_verified}
                  onChange={(v) => setForm((f) => ({ ...f, is_verified: v }))}
                />
                {saveError && (
                  <div className="rounded-lg bg-[#FEF2F2] px-3 py-2.5 text-xs text-[#DC2626]">
                    {saveError}
                  </div>
                )}
              </div>
            ) : (
              // ── View ──
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                  <Field label={t("admin.superUser.userDetail.created")}>
                    {fmtDate(detail.created_at)}
                  </Field>
                  <Field label={t("admin.superUser.userDetail.lastLogin")}>
                    {detail.last_login_at ? (
                      fmtDateTime(detail.last_login_at)
                    ) : (
                      <span className="text-[#9CA3AF]">
                        {t("admin.superUser.userDetail.never")}
                      </span>
                    )}
                  </Field>
                  <Field label={t("admin.superUser.userDetail.updatedLabel")}>
                    {fmtDate(detail.updated_at)}
                  </Field>
                  <Field label={t("admin.superUser.userDetail.workspaces")}>
                    {detail.workspace_count ?? detail.workspaces?.length ?? 0}
                  </Field>
                </div>

                {/* Workspaces + roles */}
                <div>
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
                    {t("admin.superUser.userDetail.workspaces")}
                  </p>
                  {detail.workspaces?.length ? (
                    <div className="overflow-hidden rounded-lg border border-[#F3F4F6]">
                      {detail.workspaces.map((w, i) => (
                        <div
                          key={w.id ?? i}
                          className={`flex items-center justify-between px-3 py-2 ${
                            i !== detail.workspaces.length - 1
                              ? "border-b border-[#F3F4F6]"
                              : ""
                          }`}
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <Building2
                              size={13}
                              className="flex-shrink-0 text-[#9CA3AF]"
                            />
                            <span className="truncate text-[13px] text-[#374151]">
                              {w.name}
                            </span>
                          </span>
                          {w.role && (
                            <span className="flex-shrink-0 rounded bg-[#F3F4F6] px-1.5 py-0.5 text-[10px] font-medium capitalize text-[#6B7280]">
                              {t(`admin.superUser.userDetail.roles.${w.role}`, {
                                defaultValue: w.role,
                              })}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[13px] text-[#9CA3AF]">
                      {t("admin.superUser.userDetail.noWorkspaces")}
                    </p>
                  )}
                </div>

                {/* Activity */}
                <div>
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
                    {t("admin.superUser.userDetail.activity")}
                  </p>
                  <ActivityTimeline userId={userId} />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[#F3F4F6] p-4">
            {editing ? (
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setEditing(false)}
                  disabled={saving}
                  className="rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-60"
                >
                  {t("common.actions.cancel")}
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="flex items-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-60"
                  style={{
                    background:
                      "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  }}
                >
                  {saving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Check size={14} />
                  )}
                  {t("common.actions.saveChanges")}
                </button>
              </div>
            ) : confirmDelete ? (
              <div className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-3.5">
                <div className="mb-3 flex items-start gap-2">
                  <AlertTriangle
                    size={16}
                    className="mt-0.5 flex-shrink-0 text-[#DC2626]"
                  />
                  <p className="text-[13px] text-[#991B1B]">
                    {t("admin.superUser.userDetail.deleteConfirm", {
                      name: fullName(detail),
                    })}
                  </p>
                </div>
                {deleteError && (
                  <p className="mb-3 text-[12px] font-medium text-[#DC2626]">
                    {deleteError}
                  </p>
                )}
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => {
                      setConfirmDelete(false);
                      setDeleteError(null);
                    }}
                    disabled={deleting}
                    className="rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-60"
                  >
                    {t("common.actions.cancel")}
                  </button>
                  <button
                    onClick={doDelete}
                    disabled={deleting}
                    className="flex items-center gap-1.5 rounded-lg bg-[#DC2626] px-3.5 py-2 text-sm font-semibold text-white transition duration-150 hover:bg-[#B91C1C] disabled:opacity-60"
                  >
                    {deleting ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                    {t("admin.superUser.userDetail.deletePermanently")}
                  </button>
                </div>
              </div>
            ) : confirmReset ? (
              <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3.5">
                <div className="mb-3 flex items-start gap-2">
                  <KeyRound
                    size={16}
                    className="mt-0.5 flex-shrink-0 text-[#6B7280]"
                  />
                  <p className="text-[13px] text-[#374151]">
                    <Trans
                      i18nKey="admin.superUser.userDetail.resetConfirm"
                      values={{ email: detail.email }}
                      components={{ bold: <span className="font-medium" /> }}
                    />
                  </p>
                </div>
                {resetError && (
                  <p className="mb-3 text-[12px] font-medium text-[#DC2626]">
                    {resetError}
                  </p>
                )}
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => {
                      setConfirmReset(false);
                      setResetError(null);
                    }}
                    disabled={resetting}
                    className="rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-60"
                  >
                    {t("common.actions.cancel")}
                  </button>
                  <button
                    onClick={sendReset}
                    disabled={resetting}
                    className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold text-white transition duration-150 disabled:opacity-60"
                    style={{
                      background:
                        "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                    }}
                  >
                    {resetting ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <KeyRound size={14} />
                    )}
                    {t("admin.superUser.userDetail.sendResetEmail")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={isSelf}
                  title={
                    isSelf
                      ? t("admin.superUser.userDetail.cantDeleteSelf")
                      : t("admin.superUser.userDetail.deleteUser")
                  }
                  className="flex items-center gap-1.5 rounded-[10px] border border-[#FECACA] bg-white px-3.5 py-2.5 text-sm font-medium text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 size={14} />
                  {t("common.actions.delete")}
                </button>
                <button
                  onClick={() => setConfirmReset(true)}
                  className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-3.5 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                >
                  <KeyRound size={14} />
                  {t("admin.superUser.userDetail.sendPasswordReset")}
                </button>
                <button
                  onClick={() => setConfirmSignOut(true)}
                  className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-3.5 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                >
                  <LogOut size={14} />
                  {t("admin.superUser.userDetail.signOutEverywhere")}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}
      <ConfirmDialog
        open={confirmSignOut}
        title={t("admin.superUser.userDetail.signOutEverywhereTitle")}
        body={t("admin.superUser.userDetail.signOutEverywhereBody")}
        confirmLabel={t("admin.superUser.userDetail.signOutEverywhere")}
        danger
        onConfirm={handleSignOutEverywhere}
        onCancel={() => setConfirmSignOut(false)}
      />
    </Modal>
  );
};

export default UserDetailModal;
